/* eslint-disable import/no-unused-modules */
import { v4 as uuid } from 'uuid';

import { RLearningStatus, RLearningV2ByIdQuery } from '@/store/v2';
import { Course } from '@/features/course/admin/types/course';
import { minutesToHoursMinutes } from '@/utils/time';
import { Activity, ActivityType, ActivityContent } from '@/features/course/admin/types/activity';
import { ActivityId, CourseId } from '@/features/course/admin/types/brandedIds';
import { LearningVisibility } from '@/types/learning';
import { RESULTS_PAGE_ID } from '@/features/course/admin/utils/pageTransform';
import { getQuestions } from '@/features/course/admin/atoms/CoursePageContentQuiz/CoursePageContentQuiz';

const DEFAULT_RICH_TEXT_PAGE_VALUE = () => `[{"id":"${uuid()}","type":"paragraph","children":[{"text":""}]}]`;
const DEFAULT_RICH_TEXT_QUIZ_VALUE = () =>
  `[{"children":[{"text":""}],"type":"quizQuestion","questionType":{"label":"Select one option","value":"SINGLE_SELECT","icon":["far","circle-dot"]},"title":"","version":"${uuid()}","answers":[{"label":"","isCorrect":false,"id":"${uuid()}"},{"label":"","isCorrect":false,"id":"${uuid()}"},{"label":"","isCorrect":true,"id":"${uuid()}"}]},{"id":"${uuid()}","type":"paragraph","children":[{"text":""}]}]`;

const DEFAULT_RICH_TEXT_VALUE_BY_TYPE: Record<string, () => string> = {
  [ActivityType.Page]: DEFAULT_RICH_TEXT_PAGE_VALUE,
  [ActivityType.Quiz]: DEFAULT_RICH_TEXT_QUIZ_VALUE,
  [ActivityType.Assignment]: DEFAULT_RICH_TEXT_PAGE_VALUE,
};

export const createActivity = ({
  type,
  order = 0,
  parentActivityId,
  initialTitle,
}: {
  order?: number;
  parentActivityId?: string;
  type: ActivityType;
  initialTitle: string;
}): Activity => {
  const text = DEFAULT_RICH_TEXT_VALUE_BY_TYPE[type]?.() || undefined;

  return {
    id: `activity-r-${uuid()}` as never, // todo type it
    type,
    content: {
      title: initialTitle,
      text,
      questions: type === ActivityType.Quiz ? getQuestions(text) : undefined,
      version: type === ActivityType.Quiz ? uuid() : undefined,
    } as ActivityContent,
    order,
    parentActivityId,
  };
};

export const createResultActivity = ({ course, order }: { course?: Course; order: number }): Activity => {
  return {
    id: RESULTS_PAGE_ID as ActivityId,
    type: ActivityType.Result,
    content: {
      title: 'Results',
      level: course?.level,
      effortHours: course?.effortHours,
      effortMinutes: course?.effortMinutes,
    } as ActivityContent,
    active: false,
    hasError: false,
    order,
  } as Activity;
};

const transformActivitiesFromRemote = (activities?: RLearningV2ByIdQuery['learningV2']['activities']): Activity[] => {
  const parsedActivities: Activity[] =
    activities?.map(
      (remoteActivity) =>
        ({
          id: `activity-r-${remoteActivity?.id || 0}`,
          remoteId: remoteActivity.id,
          parentActivityId: remoteActivity.parentActivityId && `activity-r-${remoteActivity?.parentActivityId || 0}`,
          remoteParentActivityId: remoteActivity.parentActivityId,
          order: remoteActivity.order,
          type: remoteActivity.type, // TODO map type
          content: remoteActivity.data,
        }) as unknown as Activity,
    ) || [];

  return parsedActivities;
};

export const transformFromRemoteLearningV2 = (
  rLearning: RLearningV2ByIdQuery['learningV2'] | undefined,
): [Course | undefined, Activity[] | undefined] => {
  if (!rLearning) return [undefined, undefined];

  const { hours, minutes } = minutesToHoursMinutes(rLearning?.effort || 0);

  return [
    {
      id: rLearning.id as unknown as CourseId,
      spaceId: rLearning.spaceId || 0,
      urnV1: `urn:collegial:${rLearning.id}`,
      urnV2: rLearning.urn,
      title: rLearning.title || '',
      effortHours: hours,
      effortMinutes: minutes,
      image: rLearning.image || '',
      language: rLearning.languageId || 0,
      meta: rLearning.meta,
      level: rLearning?.meta?.level || 'Basic',
      attributes: rLearning?.meta?.attributes,
      userRoleConnections: rLearning?.meta?.connections,
      partner: '', // tood,
      subjectId: rLearning?.subjects?.[0]?.subjectId,
      contentOwnerId: rLearning.contentOwnerId,
      teaser: rLearning.teaser || '',
      visibility: rLearning.visibility as unknown as LearningVisibility,
      status: rLearning.status as unknown as RLearningStatus,
      userLastParticipation: rLearning?.userLastParticipation,
      updatedAt: rLearning.updatedAt || '',
      updateByUserFirstName: rLearning.updateByUser?.firstname || '',
      updateByUserLastName: rLearning.updateByUser?.lastname || '',
    } as Course,
    transformActivitiesFromRemote(rLearning.activities),
  ];
};
